const NAV_ANIM_MS = 220;
window.NAV_FORCE_FIRST_MENU_OPEN = window.NAV_FORCE_FIRST_MENU_OPEN || false;

class NavMenu extends HTMLElement {
  connectedCallback() {
    if (window.innerWidth < 992) return;

    // viewport-root lives on the header element (full width), not inside nav-menu
    const header   = this.closest('header, .page-header');
    this.viewport  = (header || document).querySelector('.nav-menu_viewport');
    this.viewportRoot = this.viewport ? this.viewport.parentElement : null;
    this.indicator = this.querySelector('.nav-menu_indicator');
    if (!this.viewport) return;

    this.activeIndex   = null;
    this.closeTimer    = null;
    this.intentTimer   = null;
    this.rootHideTimer = null;
    this.exitTimers    = new Map();
    this.entries       = [];
    this._mirrorEl     = null;
    this._mirrorItems  = [];

    if (this.viewportRoot) {
      this.viewportRoot.hidden = true;
      this.viewportRoot.setAttribute('aria-hidden', 'true');
    }

    const items = this.querySelectorAll(
      '.main-navigation--desktop > .main-navigation_item[data-nav-index]'
    );

    items.forEach((item, i) => {
      const dc      = item.querySelector(':scope > dropdown-component');
      if (!dc) return;
      const wrapper = dc.querySelector('.dropdown-component_wrapper');
      const btn     = dc.querySelector('.dropdown-component_opener');
      if (!wrapper || !btn) return;
      wrapper.classList.add('nav-menu_panel');
      wrapper.dataset.state = 'closed';
      this.viewport.appendChild(wrapper);
      this.entries.push({ btn, panel: wrapper });
      btn.addEventListener('mouseenter', () => {
        // If a menu is already open, switch immediately — no delay needed.
        // If coming from a closed state, wait 40ms to filter cursor sweeps.
        if (this.activeIndex !== null) {
          clearTimeout(this.intentTimer);
          this.activate(i);
        } else {
          this.intentTimer = setTimeout(() => this.activate(i), 40);
        }
      });
      btn.addEventListener('mouseleave', () => clearTimeout(this.intentTimer));
      btn.addEventListener('focus',      () => this.activate(i));
      // Prevent DropdownComponent's own click handler from interfering
      btn.addEventListener('click', e => e.stopImmediatePropagation(), true);
    });

    // Align mega menu content with the .main-navigation element's left edge
    requestAnimationFrame(() => {
      const header  = this.viewport.closest('.page-header_desktop');
      if (!header) return;
      const nav        = this.querySelector('.main-navigation--desktop') || this;
      const navRect    = nav.getBoundingClientRect();
      const headerRect = header.getBoundingClientRect();
      this.viewport.style.setProperty('--mega-menu-indent', (navRect.left - headerRect.left) + 'px');
    });

    this.addEventListener('mouseleave', () => this.scheduleClose());
    this.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));

    this.viewport.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
    this.viewport.addEventListener('mouseleave', () => this.scheduleClose());

    this.viewport.addEventListener('click', e => {
      if (e.target.closest('a')) this.closeNow();
    });

    document.addEventListener('keyup', e => {
      if (e.key !== 'Escape' || this.activeIndex === null) return;
      this.entries[this.activeIndex].btn.focus();
      this.closeNow();
    });

    if (window.NAV_FORCE_FIRST_MENU_OPEN) {
      requestAnimationFrame(() => this.activate(0));
    }
  }

  activate(index) {
    if (window.NAV_FORCE_FIRST_MENU_OPEN) index = 0;

    clearTimeout(this.closeTimer);
    clearTimeout(this.rootHideTimer);
    if (this.activeIndex === index) return;

    if (this.viewportRoot) {
      this.viewportRoot.hidden = false;
      this.viewportRoot.setAttribute('aria-hidden', 'false');
    }

    const isFirst = this.activeIndex === null;
    const goRight = !isFirst && index > this.activeIndex;

    // Snap any mid-exit panels to closed so rapid switching never leaves
    // multiple panels simultaneously visible.
    this.entries.forEach(({ panel }) => {
      if (panel.dataset.state === 'exiting') {
        clearTimeout(this.exitTimers.get(panel));
        this.exitTimers.delete(panel);
        panel.dataset.state  = 'closed';
        panel.dataset.motion = '';
      }
    });

    // Exit the current panel with a directional animation
    if (this.activeIndex !== null) {
      const prev = this.entries[this.activeIndex];
      prev.btn.setAttribute('aria-expanded', 'false');
      this._startExit(prev.panel, goRight ? 'to-start' : 'to-end');
    }

    const entry = this.entries[index];
    if (!entry) return;

    // Height animates when switching between open panels; snaps on first open.
    if (!isFirst) {
      this.viewport.dataset.switching = 'true';
    } else {
      delete this.viewport.dataset.switching;
    }

    this._rotatePromoCards(entry.panel);
    this._sizeViewport(entry.panel);

    const motion = isFirst ? 'initial' : (goRight ? 'from-end' : 'from-start');
    entry.panel.dataset.motion = motion;
    entry.panel.dataset.state  = 'open';
    this.viewport.dataset.state = 'open';

    entry.btn.setAttribute('aria-expanded', 'true');
    this._positionIndicator(entry.btn);
    this._syncHeadings(entry.panel, motion);
    this.activeIndex = index;
  }

  _rotatePromoCards(panel) {
    const groups = panel.querySelectorAll('[data-rq-promo-group]');

    groups.forEach(group => {
      const cards = [...group.querySelectorAll('[data-rq-promo-card]')];
      if (!cards.length) return;

      let randomIndex = Math.floor(Math.random() * cards.length);
      const previousIndex = Number(group.dataset.rqPromoLastIndex);

      if (cards.length > 1 && randomIndex === previousIndex) {
        randomIndex = (randomIndex + 1 + Math.floor(Math.random() * (cards.length - 1))) % cards.length;
      }

      group.dataset.rqPromoLastIndex = String(randomIndex);

      cards.forEach((card, index) => {
        card.hidden = index !== randomIndex;
      });
    });
  }

  // Maintain a persistent headings layer above the animated panels.
  // Only updates heading elements whose text actually changed, so headings
  // that are identical across panels never re-render or appear to animate.
  _syncHeadings(panel, motion) {
    // Build the mirror container once
    if (!this._mirrorEl) {
      this._mirrorEl = document.createElement('div');
      this._mirrorEl.className = 'nav-menu_headings-mirror';
      this._mirrorEl.setAttribute('aria-hidden', 'true');
      this.viewport.appendChild(this._mirrorEl);
    }
    this._mirrorEl.hidden = false;

    const labels = [...panel.querySelectorAll('.rq-mega-menu_group-label')];

    // Direction vars used for both excess-heading exits and text-change animations
    const fromX = motion === 'from-end' ? '40px'  : motion === 'from-start' ? '-40px' : null;
    const toX   = motion === 'from-end' ? '-24px' : motion === 'from-start' ? '24px'  : null;

    // Add missing mirror items
    while (this._mirrorItems.length < labels.length) {
      const p = document.createElement('p');
      p.className = 'rq-mega-menu_group-label';
      p.style.position = 'absolute';
      this._mirrorEl.appendChild(p);
      this._mirrorItems.push({ el: p, text: null });
    }
    // Remove excess mirror items — slide out like list items, then remove.
    // Clear any inline animation first so inline styles don't override.
    while (this._mirrorItems.length > labels.length) {
      const excess = this._mirrorItems.pop();
      excess.el.style.animation = '';
      if (toX !== null) {
        excess.el.style.setProperty('--nav-item-to-x', toX);
        excess.el.style.animation = 'rq-nav-item-out 220ms ease-in both';
      }
      setTimeout(() => excess.el.remove(), 280);
    }

    // Update text only when it has changed; animate if switching direction
    labels.forEach((label, i) => {
      const text = label.textContent.trim();
      const item = this._mirrorItems[i];
      if (item.text !== text) {
        if (fromX !== null && item.text !== null) {
          // Animate the outgoing heading: clone it, play exit, then remove
          const clone = item.el.cloneNode(true);
          clone.style.setProperty('--nav-item-to-x', toX);
          clone.style.animation = 'rq-nav-item-out 220ms ease-in both';
          this._mirrorEl.appendChild(clone);
          setTimeout(() => clone.remove(), 280);
        }

        item.text = text;
        item.el.textContent = text;

        if (fromX !== null) {
          item.el.style.animation = 'none';
          requestAnimationFrame(() => {
            item.el.style.setProperty('--nav-item-from-x', fromX);
            item.el.style.animation = 'rq-nav-item-in 380ms cubic-bezier(.37,.01,0,.98) 160ms both';
          });
        }
      }
    });

    // Position mirror items to match actual label positions
    requestAnimationFrame(() => {
      if (!this._mirrorEl) return;
      const mirrorRect = this._mirrorEl.getBoundingClientRect();
      labels.forEach((label, i) => {
        const rect = label.getBoundingClientRect();
        const el = this._mirrorItems[i].el;
        el.style.top  = (rect.top  - mirrorRect.top)  + 'px';
        el.style.left = (rect.left - mirrorRect.left) + 'px';
      });
    });
  }

  _startExit(panel, motion) {
    clearTimeout(this.exitTimers.get(panel));
    panel.dataset.motion = motion;
    panel.dataset.state  = 'exiting';
    const t = setTimeout(() => {
      if (panel.dataset.state === 'exiting') {
        panel.dataset.state  = 'closed';
        panel.dataset.motion = '';
      }
    }, NAV_ANIM_MS);
    this.exitTimers.set(panel, t);
  }

  _sizeViewport(panel) {
    // Measure the panel height so the viewport can animate between panel sizes.
    void this.viewport.offsetHeight; // force reflow
    const h = panel.offsetHeight;
    if (h) this.viewport.style.setProperty('--nav-panel-h', h + 'px');
  }

  _positionIndicator(btn) {
    if (!this.indicator) return;
    const navRect = this.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    this.indicator.style.left  = (btnRect.left - navRect.left) + 'px';
    this.indicator.style.width = btnRect.width + 'px';
    this.indicator.dataset.visible = 'true';
  }

  scheduleClose() {
    if (window.NAV_FORCE_FIRST_MENU_OPEN) return;

    this.closeTimer = setTimeout(() => this.closeNow(), 150);
  }

  closeNow() {
    if (window.NAV_FORCE_FIRST_MENU_OPEN) {
      this.activate(0);
      return;
    }

    clearTimeout(this.closeTimer);
    if (this.activeIndex !== null) {
      const { panel, btn } = this.entries[this.activeIndex];
      panel.dataset.state  = 'closed';
      panel.dataset.motion = '';
      btn.setAttribute('aria-expanded', 'false');
    }
    this.viewport.dataset.state = 'closed';
    delete this.viewport.dataset.switching;
    if (this.indicator) this.indicator.dataset.visible = 'false';
    this.activeIndex = null;

    clearTimeout(this.rootHideTimer);
    this.rootHideTimer = setTimeout(() => {
      if (!this.viewportRoot || this.activeIndex !== null) return;
      this.viewportRoot.hidden = true;
      this.viewportRoot.setAttribute('aria-hidden', 'true');
    }, NAV_ANIM_MS);

    // Wipe the headings mirror so stale headings from a longer menu
    // don't flash briefly when a shorter menu next opens.
    if (this._mirrorEl) {
      this._mirrorEl.innerHTML = '';
      this._mirrorItems = [];
    }
  }
}

customElements.define('nav-menu', NavMenu);
